import {FormEvent, SyntheticEvent, useId, useRef, useState} from "react";
import {Button, ButtonGroup, Card, Form, Modal, ProgressBar} from "react-bootstrap";
import {ProductsApi} from "../../../api/ProductsApi";
import {ProductImageViewModel, ProductViewModel} from "../../../api/models";
import {uploadToPresignedUrl} from "../../../api/upload";
import {ErrorAlert} from "../../../components/ErrorAlert";
import {ConfirmModal} from "../../../components/ConfirmModal";
import {SubmitButton} from "../../../components/SubmitButton";
import {readImageSize, sizeOf} from "../../../components/imageSize";
import {isAnimatedImage} from "../../../components/imageAnimation";

// The API's image types: static ones (no SVG or GIF). PNG and WebP can be animated too, which imageProblem refuses.
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

// Why the file can't be a product image, if it can't.
async function imageProblem(file: File): Promise<string | undefined> {
    if (!IMAGE_TYPES.includes(file.type)) {
        return 'Use a PNG, JPEG or WebP image.';
    }
    return await isAnimatedImage(file) ? "That image is animated. Use a still one." : undefined;
}

// The chosen file and what's wrong with it. The check reads the file, so until it's done the file counts as a problem.
function useImageFile() {
    const [file, setFile] = useState<File>();
    const [problem, setProblem] = useState<string>();
    const [checking, setChecking] = useState(false);
    const latest = useRef<File | undefined>(undefined);
    const choose = async (chosen?: File) => {
        latest.current = chosen;
        setFile(chosen);
        setProblem(undefined);
        setChecking(!!chosen);
        if (!chosen) {
            return;
        }
        const found = await imageProblem(chosen).catch(() => "That file can't be read.");
        if (latest.current === chosen) { // not since replaced by another choice
            setProblem(found);
            setChecking(false);
        }
    };
    return { file, problem, ready: !!file && !problem && !checking, choose };
}

// A product's images, served by the CDN. The first one is the product's picture in listings. Each one's size goes to
// the API with it, for the site to keep its place while it loads; an image from before that gets it from its thumbnail
// here, once it has loaded.
export function ProductImages({product, onChanged}: { product: ProductViewModel, onChanged(): void }) {
    const {file, problem: fileProblem, ready, choose} = useImageFile();
    const [altText, setAltText] = useState('');
    const [progress, setProgress] = useState<number>();
    const [deleting, setDeleting] = useState<ProductImageViewModel>();
    const [editing, setEditing] = useState<ProductImageViewModel>();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const [formKey, setFormKey] = useState(0);
    const measured = useRef(new Set<string>());

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true);
        setError(undefined);
        try {
            await action();
            onChanged();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setProgress(undefined);
        }
    };

    const add = (e: FormEvent) => {
        e.preventDefault();
        if (!file || !ready) {
            return;
        }
        void run(async () => {
            setProgress(0);
            const {upload} = await ProductsApi.addImage(product.id, file.type, altText.trim(), await readImageSize(file));
            await uploadToPresignedUrl(upload, file, setProgress);
            void choose(undefined);
            setAltText('');
            setFormKey(x => x + 1); // clears the file input
        });
    };

    // Swaps sort orders with the neighbour, renumbering first so equal orders can't stall it.
    const move = (index: number, by: -1 | 1) => run(async () => {
        const order = [...product.images];
        [order[index], order[index + by]] = [order[index + by], order[index]];
        for (const [i, image] of order.entries()) {
            if (image.sortOrder !== i) {
                await ProductsApi.updateImage(product.id, image.id, { sortOrder: i });
            }
        }
    });

    // Quietly: an image without a size still shows, only with a jump as it loads.
    const recordSize = (image: ProductImageViewModel, e: SyntheticEvent<HTMLImageElement>) => {
        const size = sizeOf(e.currentTarget);
        if (image.width || !size || measured.current.has(image.id)) {
            return;
        }
        measured.current.add(image.id);
        ProductsApi.updateImage(product.id, image.id, size).catch(() => measured.current.delete(image.id));
    };

    return (
        <Card>
            <Card.Header>Images</Card.Header>
            <Card.Body>
                <ErrorAlert error={error} onClose={() => setError(undefined)}/>
                {product.images.length === 0 && <p className="text-body-secondary">No images yet.</p>}
                <div className="d-flex flex-wrap gap-3 mb-3">
                    {product.images.map((image, index) => (
                        <div key={image.id} className="product-image-tile">
                            <img src={image.url} alt={image.altText} className="img-thumbnail mb-1" onLoad={e => recordSize(image, e)}/>
                            <div className="small text-truncate text-body-secondary" title={image.altText}>{image.altText || 'No description'}</div>
                            <ButtonGroup size="sm">
                                <Button variant="outline-secondary" disabled={busy || index === 0} onClick={() => move(index, -1)} aria-label="Move earlier">
                                    <i className="bi bi-arrow-left" aria-hidden="true"></i>
                                </Button>
                                <Button variant="outline-secondary" disabled={busy || index === product.images.length - 1} onClick={() => move(index, 1)} aria-label="Move later">
                                    <i className="bi bi-arrow-right" aria-hidden="true"></i>
                                </Button>
                                <Button variant="outline-secondary" disabled={busy} onClick={() => setEditing(image)} aria-label="Edit">
                                    <i className="bi bi-pencil" aria-hidden="true"></i>
                                </Button>
                                <Button variant="outline-danger" disabled={busy} onClick={() => setDeleting(image)} aria-label="Delete">
                                    <i className="bi bi-trash" aria-hidden="true"></i>
                                </Button>
                            </ButtonGroup>
                        </div>
                    ))}
                </div>
                <Form key={formKey} onSubmit={add} className="d-flex flex-wrap gap-2 align-items-start">
                    <Form.Group controlId="image-file" className="flex-grow-1">
                        <Form.Control type="file" accept={IMAGE_TYPES.join(',')} aria-label="Image file" isInvalid={!!fileProblem}
                                      aria-invalid={!!fileProblem || undefined} aria-describedby={fileProblem ? 'image-file-error' : undefined}
                                      onChange={e => void choose((e.target as HTMLInputElement).files?.[0])}/>
                        <Form.Control.Feedback type="invalid" id="image-file-error">{fileProblem}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Control className="flex-grow-1 w-auto" placeholder="Description (alt text)" aria-label="Description"
                                  value={altText} onChange={e => setAltText(e.target.value)}/>
                    <SubmitButton busy={busy}>Add image</SubmitButton>
                </Form>
                {progress !== undefined && <ProgressBar className="mt-2" now={progress * 100}/>}
            </Card.Body>
            {editing && (
                <ImageModal product={product} image={editing} onHide={() => setEditing(undefined)}
                            onSaved={() => { setEditing(undefined); onChanged(); }}/>
            )}
            <ConfirmModal show={!!deleting} title="Delete this image?" confirmLabel="Delete" busy={busy}
                          onConfirm={() => { const image = deleting!; setDeleting(undefined); void run(() => ProductsApi.removeImage(product.id, image.id)); }}
                          onCancel={() => setDeleting(undefined)}>
                {deleting && <img src={deleting.url} alt={deleting.altText} className="img-thumbnail product-image-preview"/>}
            </ConfirmModal>
        </Card>
    );
}

// Edits an image's description (alt text), and replaces its picture, keeping the description and its place.
function ImageModal({product, image, onHide, onSaved}: {
    product: ProductViewModel,
    image: ProductImageViewModel,
    onHide(): void,
    onSaved(): void
}) {
    const titleId = useId();
    const [altText, setAltText] = useState(image.altText);
    const {file, problem: fileProblem, ready, choose: chooseFile} = useImageFile();
    const [preview, setPreview] = useState<string>();
    const [progress, setProgress] = useState<number>();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const choose = (chosen?: File) => {
        if (preview) {
            URL.revokeObjectURL(preview);
        }
        setPreview(chosen && IMAGE_TYPES.includes(chosen.type) ? URL.createObjectURL(chosen) : undefined);
        void chooseFile(chosen);
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        if (file && !ready) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            if (altText.trim() !== image.altText) {
                await ProductsApi.updateImage(product.id, image.id, { altText: altText.trim() });
            }
            if (file) {
                setProgress(0);
                const {upload} = await ProductsApi.replaceImage(product.id, image.id, file.type, await readImageSize(file));
                await uploadToPresignedUrl(upload, file, setProgress);
                await ProductsApi.completeImageReplacement(product.id, image.id);
            }
            onSaved();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setProgress(undefined);
        }
    };

    return (
        <Modal aria-labelledby={titleId} show onHide={busy ? () => undefined : onHide} centered>
            <Form noValidate onSubmit={submit}>
                <Modal.Header closeButton={!busy}><Modal.Title id={titleId}>Edit image</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error}/>
                    <img src={preview ?? image.url} alt={altText} className="img-thumbnail product-image-preview mb-3"/>
                    <Form.Group className="mb-3" controlId="image-edit-alt">
                        <Form.Label>Description (alt text)</Form.Label>
                        <Form.Control value={altText} disabled={busy} onChange={e => setAltText(e.target.value)}/>
                    </Form.Group>
                    <Form.Group controlId="image-edit-file">
                        <Form.Label>Replace the picture <span className="text-body-secondary">(optional)</span></Form.Label>
                        <Form.Control type="file" accept={IMAGE_TYPES.join(',')} disabled={busy} isInvalid={!!fileProblem}
                                      aria-invalid={!!fileProblem || undefined} aria-describedby={fileProblem ? 'image-edit-file-error' : 'image-edit-file-help'}
                                      onChange={e => choose((e.target as HTMLInputElement).files?.[0])}/>
                        <Form.Control.Feedback type="invalid" id="image-edit-file-error">{fileProblem}</Form.Control.Feedback>
                        {!fileProblem && <Form.Text id="image-edit-file-help">It keeps its description and place. The old picture is deleted.</Form.Text>}
                    </Form.Group>
                    {progress !== undefined && <ProgressBar className="mt-3" now={progress * 100}/>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>Save</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
